/* eslint-disable react-native/no-inline-styles */
import { cloneDeep } from 'lodash';
import React, { ChangeEvent, FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { StyleSheet, View, TouchableOpacity, Platform, StyleProp, TextStyle, ActivityIndicator } from 'react-native';
import { InputPickerHelper } from '../../../apps/web/src/administration/components/worksheet/inputPickers/input-picker-helper';
import { WorksheetCharacterLengthValidation } from '../../../apps/web/src/administration/views/worksheets/utils';
import colors from '@packages/core/styles/colors';
import {
    DraggableWorksheetFormComponent,
    FormAnswerSelectedOption,
    FormElementOption,
    FormElementTypeId,
    SelectedOption,
    WorksheetAnswerElement,
    FormElementFile,
    AssetProcessingStateId,
    WorksheetElementAnswers,
    FormElementGridCellAnswer,
    ConditionalElement,
} from '@packages/models/api';
import { DropResult } from 'react-beautiful-dnd';
import {
    TextInputHelper,
    TextareaHelper,
    Typography,
    Icon,
    Range,
    Collapse,
    StsIconName,
    TextBadge,
    SuccessFailBadge,
    Button,
} from '../shared';
import { AssetEditorModal } from './AssetEditorModal';
import { BaseWorksheetFormElement } from './base-worksheet-form-element';
import { ConditionalOptions } from './conditional-options';
import { DynamicGrid } from './worksheet-form-elements/dynamic-grid';
import { DynamicGridRowType } from 'web/src/administration/components/worksheet/dynamic-grid/dynamic-grid-builder';
import { hasUnansweredRequiredCells } from './utils';
import { PressureTestGrid, SubRowGridType } from './worksheet-form-elements/pressure-test-grid';
import { TireWearRadio } from './worksheet-form-elements/tire-wear-radio';
import { useAnswerHandle } from './useAnswerHandle';
import { useGridElement } from './useGridElement';
import { useValidation } from './useValidation';
import { ValveClearanceGrid, SubColumnGridType } from './worksheet-form-elements/valve-clearance-grid';
import gridImage from '../assets/images/carNoiseSpottingImage.png';
import Select from '../../../apps/web/src/components/select';
import { useTranslation } from 'react-i18next';

/** Image file extensions recognized for inline preview rendering. */
const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg', '.heic', '.heif'];

/**
 * Check whether a file should be treated as an image based on its name or MIME type.
 * Used to decide whether to render an `<img>` preview or a generic file indicator.
 *
 * @param file - the form element file to check
 * @returns true if the file is an image
 */
function isImageFile(file: FormElementFile | undefined): boolean {
    if (!file) {
        return false;
    }

    // Check MIME type from the browser File object (available for fresh uploads)
    if (file.file?.type?.startsWith('image/')) {
        return true;
    }

    // Fall back to extension check (for server-loaded assets that only have a name)
    const name = file.name?.toLowerCase() ?? '';
    return IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
}

export type WorksheetFormElementComponent = FC<{
    isMediaViewOnly?: boolean;
    item: DraggableWorksheetFormComponent;
    readOnly?: boolean;
    preview?: boolean;
    isDraftLoading?: boolean;
    isSubmitLoading?: boolean;
    onAnswerChange?: (answer: WorksheetElementAnswers) => void;
    handleDragEnd: (result: DropResult) => void;
    onEditClick?(item: DraggableWorksheetFormComponent): void;
    onDeleteClick?(item: DraggableWorksheetFormComponent): void;
    onCopyClick?(item: DraggableWorksheetFormComponent): void;
    onChildDrag?(item: DraggableWorksheetFormComponent): void;
    handleIsValid?: (valid: boolean) => void;
    handleAnswerChange?: (formelementId: string, selectedAnswer: FormAnswerSelectedOption[]) => void;
    handleGridCellAnswerChange?: (formElementId: string, gridCellAnswer: FormElementGridCellAnswer[]) => void;
    getCurrentAnswerByElementId?: (id: string) => WorksheetElementAnswers;
}>;

type WorksheetFormFileFieldProps = FC<{
    formElementFile?: FormElementFile;
    onChange?: (file: File | undefined, overlay: boolean) => void;
    showDelete?: boolean;
    onDelete?: () => void;
    editable?: boolean;
    isMediaViewOnly?: boolean;
}>;

type FileSelectorProps = FC<{
    iconName?: StsIconName;
    onChange?: (data: File | undefined) => void;
    accept?: string;
    disabled?: boolean;
}>;
const FileSelector: FileSelectorProps = ({ onChange, iconName = 'image', accept = 'image/*', disabled = false }) => {
    const fileRef = useRef<HTMLInputElement>(null);
    const triggerShowPicker = () => {
        if (fileRef.current) {
            fileRef.current.showPicker();
        }
    };

    const onChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        onChange?.(file);
        if (fileRef.current) {
            fileRef.current.value = '';
        }
    };

    return (
        <>
            <input ref={fileRef} type="file" accept={accept} style={{ display: 'none' }} onChange={onChangeFile} />
            <Button variant="ghost-blue" size="small" disabled={disabled}>
                <Icon
                    name={iconName}
                    onPress={() => {
                        triggerShowPicker();
                    }}
                />
            </Button>
        </>
    );
};
const WorksheetFormFileField: WorksheetFormFileFieldProps = ({
    formElementFile,
    onChange,
    showDelete,
    onDelete,
    editable = true,
    isMediaViewOnly = false,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [showEdit, setShowEdit] = useState(false);
    const [showEditor, setShowEditor] = useState(false);
    const onEditImage = useCallback(
        (file: File | undefined, overlay: boolean = false) => {
            onChange?.(file, overlay);
        },
        [onChange]
    );
    const isUrlPresent = useMemo(() => {
        return formElementFile?.overlay?.url || formElementFile?.url;
    }, [formElementFile]);

    const isImage = useMemo(() => isImageFile(formElementFile), [formElementFile]);

    const isShowDelete = useMemo(() => {
        return formElementFile?.file || formElementFile?.overlay?.file;
    }, [formElementFile]);
    const isUploading = useMemo(() => {
        return formElementFile?.status === AssetProcessingStateId.UPLOADING;
    }, [formElementFile]);

    const computedStyle = {
        ...styles.imageContainer,
        ...((isUrlPresent && isImage && isLoading) || isUploading
            ? { border: `1px solid ${colors.grayFive}`, width: 200, height: 200 }
            : {}),
    };
    return (
        <div style={computedStyle}>
            {formElementFile?.status === AssetProcessingStateId.UPLOADING && (
                <ActivityIndicator style={{ margin: 10 }} color={colors.blueOne} size="large" />
            )}

            {isUrlPresent && isImage && (
                <>
                    {isLoading && <ActivityIndicator style={{ margin: 10 }} color={colors.blueOne} size="large" />}
                    <img
                        src={formElementFile?.overlay?.url || formElementFile?.url}
                        style={{
                            width: '100%',
                            maxHeight: 200,
                            objectFit: 'contain',
                            display: isLoading ? 'none' : 'block',
                        }}
                        onMouseEnter={() => {
                            setShowEdit(true);
                        }}
                        onLoad={() => {
                            setIsLoading(false);
                        }}
                    />
                    {showEdit && editable && (
                        <div
                            onMouseLeave={() => {
                                setShowEdit(false);
                            }}
                            className="gap-5"
                            style={{
                                height: '100%',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                backgroundColor: 'rgba(248, 248, 248,0.6)',
                            }}
                        >
                            <Icon name={isMediaViewOnly ? 'eye' : 'pencil'} onPress={() => setShowEditor(true)} />
                            {showDelete && isShowDelete && editable && (
                                <Icon style={{ marginLeft: 10 }} name="trash" onPress={onDelete} />
                            )}
                        </div>
                    )}
                    {showEditor && (
                        <AssetEditorModal
                            formElementFile={formElementFile!}
                            viewOnly={isMediaViewOnly}
                            onClose={() => setShowEditor(false)}
                            onSave={(file) => onEditImage(file, true)}
                        />
                    )}
                </>
            )}
            {formElementFile?.caption && <Typography variant="lead">{formElementFile.caption}</Typography>}
        </div>
    );
};

const WorksheetLabel: FC<{
    label: string | undefined;
    required?: boolean | undefined;
    style?: StyleProp<TextStyle>;
}> = ({ label, required, style }) => {
    return (
        <>
            <Typography variant="label" style={[styles.labelText, style && style]}>
                {label}
                {required && <Typography color="redOne"> *</Typography>}
            </Typography>
        </>
    );
};

const WorksheetFormTextHeader: WorksheetFormElementComponent = ({ item: { formElementFile } }) => {
    return (
        <>
            <WorksheetFormFileField formElementFile={formElementFile} editable={false} />
        </>
    );
};

type FileActionsProps = FC<{
    onChange?: (data: File | undefined) => void;
    file?: FormElementFile;
    disabled?: boolean;
    assetId?: string;
    onDeleteAsset?: () => void;
}>;
const Actions: FileActionsProps = ({ onChange, file, disabled = false, assetId, onDeleteAsset }) => {
    const aRef = useRef<HTMLAnchorElement>(null);
    const download = useCallback(() => {
        if (aRef.current && file?.name && file?.url) {
            aRef.current.href = file.url;
            aRef.current.download = file?.name;
            aRef.current.click();
        }
    }, [file]);

    const isLocalFile = useMemo(() => {
        return file?.url ? file.url.includes('localhost') : true;
    }, [file]);

    const handleCrossClick = useCallback(() => {
        if (assetId) {
            // File is saved (has assetId), call delete function
            onDeleteAsset?.();
        } else {
            // File is just selected, clear it
            onChange?.(undefined);
        }
    }, [assetId, onDeleteAsset, onChange]);

    const isCrossDisabled = disabled || (!file?.url && !assetId);

    return (
        <>
            <a ref={aRef} style={{ display: 'none' }} />
            <FileSelector iconName="upload" accept="*" onChange={onChange} disabled={disabled} />
            <Button variant="ghost-blue" size="small" onPress={download} disabled={!file?.url}>
                <Icon name="download" />
            </Button>
            <Button variant="ghost-blue" size="small" onPress={handleCrossClick} disabled={isCrossDisabled}>
                <Icon name="x-close" />
            </Button>
        </>
    );
};

//File element
const WorksheetFormFile: WorksheetFormElementComponent = ({
    item,
    isMediaViewOnly = false,
    isDraftLoading = false,
    isSubmitLoading = false,
    onAnswerChange,
    getCurrentAnswerByElementId,
    handleIsValid,
    onDeleteClick,
}) => {
    const { formElementFile, formElementId, options, required } = item;
    const { t } = useTranslation();
    const [answerAssets, setAnswerAsset] = useState<FormElementFile>();

    const { handleValidation, errorMessage } = useValidation({ handleIsValid, required });

    // Get current answer and use its asset data first (most up-to-date), then fallback to local state or original formElementFile
    const currentAnswer = getCurrentAnswerByElementId?.(formElementId);
    const currentFileData = currentAnswer?.asset?.answerFile || answerAssets || formElementFile;

    const imageName = currentFileData?.name;
    const url = currentFileData?.url;

    useEffect(() => {
        const ans = getCurrentAnswerByElementId?.(formElementId);
        setAnswerAsset(ans?.asset?.answerFile);
    }, [formElementId, getCurrentAnswerByElementId]);

    const onChange = useCallback(
        (file: File | undefined) => {
            const fileObj = {
                file,
                url: file ? URL.createObjectURL(file) : undefined,
                name: file?.name,
                // Remove the UPLOADING status - file is only selected, not uploaded yet
            };
            onAnswerChange?.({
                asset: {
                    answerFile: fileObj,
                },
                elementId: formElementId,
                selectedOptions: [],
            });
            onAnswerChange?.({
                asset: {
                    answerFile: fileObj,
                },
                elementId: formElementId,
                selectedOptions: [],
            });

            const result = handleValidation({
                currentValue: fileObj.url || '',
                prevValue: answerAssets?.url || '',
                needFileTypeValidation: true,
                fileName: fileObj?.name,
            });
            if (result !== false) {
                setAnswerAsset(fileObj);
            } else {
                // Clear the selected file when validation fails
                setAnswerAsset(undefined);
                onAnswerChange?.({
                    asset: undefined,
                    elementId: formElementId,
                    selectedOptions: [],
                });
            }
        },
        [answerAssets?.url, formElementId, handleValidation, onAnswerChange, options]
    );

    const isAnswerImage = useMemo(() => isImageFile(answerAssets), [answerAssets]);
    const answerUrl = answerAssets?.overlay?.url || answerAssets?.url;

    const isUploading = useMemo(() => {
        const uploading = isDraftLoading || isSubmitLoading;
        return uploading;
    }, [isDraftLoading, isSubmitLoading]);

    const onDeleteAsset = useCallback(() => {
        // Clear the local state
        setAnswerAsset(undefined);

        // Clear the answer in the parent component
        onAnswerChange?.({
            asset: undefined,
            elementId: formElementId,
            selectedOptions: [],
        });

        // Call the parent delete handler if provided
        onDeleteClick?.(item);
    }, [formElementId, onAnswerChange, onDeleteClick, item]);

    return (
        <>
            <TextInputHelper
                placeholder={
                    currentFileData?.assetId ? 'Select File or Image' : answerAssets?.name || 'Select File or Image'
                }
                rightIcon={
                    <Actions
                        onChange={onChange}
                        file={answerAssets}
                        disabled={isMediaViewOnly}
                        assetId={currentFileData?.assetId}
                        onDeleteAsset={onDeleteAsset}
                    />
                }
                editable={false}
                inputContainerStyle={{ backgroundColor: '#fff', marginTop: 10 }}
                errorMessage={errorMessage}
            />
            {answerUrl && isAnswerImage && (
                <div style={styles.imageContainer}>
                    <div style={{ position: 'relative' }}>
                        <img
                            src={answerUrl}
                            style={{
                                width: '100px',
                                maxHeight: 200,
                                objectFit: 'contain',
                            }}
                        />
                        {isUploading && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '0',
                                    left: '0',
                                    background: 'gray',
                                    width: '100%',
                                    height: '100%',
                                    opacity: 0.8,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                                data-testid="uploading-indicator"
                            >
                                <ActivityIndicator color={colors.blueOne} size="small" />
                            </div>
                        )}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            alignSelf: 'flex-start',
                        }}
                    >
                        <Typography variant="lead" style={{ textAlign: 'left' }}>
                            <a href={url} target="_blank" download={imageName}>
                                {imageName}
                            </a>
                        </Typography>
                    </div>
                </div>
            )}
            {(currentFileData?.assetId || formElementFile?.caption) && !isAnswerImage && (
                <div style={{ display: 'flex', gap: 15, marginTop: 10 }}>
                    <div
                        style={{
                            padding: '15px',
                            background: '#f8f8f8',
                        }}
                    >
                        <Icon name="file" size={24} />
                    </div>
                    <Typography variant="lead" style={{ textAlign: 'left' }}>
                        <a href={url} target="_blank" download={imageName}>
                            {imageName}
                        </a>
                    </Typography>

                    <Typography variant="lead" style={{ textAlign: 'left' }}>
                        {formElementFile?.caption}
                    </Typography>
                </div>
            )}
            {!currentFileData?.assetId && !isAnswerImage && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                    {answerAssets?.name && (
                        <div style={{ display: 'flex', gap: 15, marginTop: 10 }}>
                            <div
                                style={{
                                    padding: '15px',
                                    background: '#f8f8f8',
                                }}
                            >
                                {isUploading ? (
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <ActivityIndicator style={{ margin: 10 }} color={colors.blueOne} size="small" />
                                    </div>
                                ) : (
                                    <Icon name="file" size={24} />
                                )}
                            </div>
                            <Typography variant="lead" style={{ textAlign: 'left' }}>
                                {answerAssets?.name}
                            </Typography>

                            <Typography variant="lead" style={{ textAlign: 'left' }}>
                                {formElementFile?.caption}
                            </Typography>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

const WorksheetFormTextParagraph: WorksheetFormElementComponent = ({ item: { formElementFile } }) => {
    return (
        <>
            <WorksheetFormFileField formElementFile={formElementFile} editable={false} />
        </>
    );
};

const WorksheetFormInputText: WorksheetFormElementComponent = ({
    item: { formElementId, options, selectedOptions, required },
    onAnswerChange,
    readOnly,
    handleIsValid,
    handleAnswerChange,
}) => {
    const { handleValidation, errorMessage } = useValidation({ handleIsValid, required });
    const { handleAnswer } = useAnswerHandle({ handleAnswerChange, onAnswerChange });
    const [value, setValue] = useState(selectedOptions && selectedOptions[0] ? selectedOptions[0].answerText : '');

    return (
        <>
            {[options?.[options?.length - 1]].map((option) => {
                return (
                    <View key={option.optionId}>
                        <TextInputHelper
                            key={option.optionId}
                            required={required}
                            value={value}
                            editable={!readOnly}
                            errorMessage={errorMessage}
                            onChangeText={(text) => {
                                if (text.trim().length > WorksheetCharacterLengthValidation.AnswerLength) {
                                    return;
                                }
                                handleValidation({ currentValue: text, prevValue: value || '' });
                                const answerOption = [
                                    {
                                        optionId: option.optionId,
                                        answerText: text,
                                        optionLabel: option.optionLabel,
                                    },
                                ];
                                handleAnswer(formElementId, answerOption);
                                setValue(text);
                            }}
                            placeholder={option.optionPlaceholder}
                        />
                    </View>
                );
            })}
        </>
    );
};

const WorksheetFormInputTextarea: WorksheetFormElementComponent = ({
    item: { formElementId, options, selectedOptions, required },
    onAnswerChange,
    readOnly,
    handleIsValid,
    handleAnswerChange,
}) => {
    const { handleValidation, errorMessage } = useValidation({ handleIsValid, required });
    const { handleAnswer } = useAnswerHandle({ handleAnswerChange, onAnswerChange });
    const [value, setValue] = useState(selectedOptions && selectedOptions[0] ? selectedOptions[0].answerText : '');

    return (
        <>
            {[options?.[options?.length - 1]].map((option) => {
                return (
                    <View key={option.optionId}>
                        <TextareaHelper
                            key={option.optionId}
                            value={value}
                            required={required}
                            editable={!readOnly}
                            errorMessage={errorMessage}
                            onChangeText={(text) => {
                                if (text.trim().length > WorksheetCharacterLengthValidation.AnswerLength) {
                                    return;
                                }
                                handleValidation({ currentValue: text, prevValue: value || '' });
                                const answerOption = [
                                    {
                                        optionId: option.optionId,
                                        optionLabel: option.optionLabel,
                                        answerText: text,
                                    },
                                ];
                                setValue(text);
                                handleAnswer(formElementId, answerOption);
                            }}
                            placeholder={option.optionPlaceholder}
                        />
                    </View>
                );
            })}
        </>
    );
};
const WorksheetTime: WorksheetFormElementComponent = ({
    item: { options, formElementFile, formElementId, selectedOptions, required },
    readOnly,
    preview,
    onAnswerChange,
    handleIsValid,
    handleAnswerChange,
}) => {
    const { handleValidation, errorMessage } = useValidation({ handleIsValid, required });
    const { handleAnswer } = useAnswerHandle({ handleAnswerChange, onAnswerChange });

    const [value, setValue] = useState<string>('');

    useEffect(() => {
        const selectedValue = selectedOptions?.[0]?.answerText;
        setValue(selectedValue || '');
    }, [selectedOptions]);
    return (
        <>
            <WorksheetFormFileField formElementFile={formElementFile} editable={false} />
            <InputPickerHelper
                type={'time'}
                iconName={'clock'}
                data={value}
                errorMessage={errorMessage}
                onChange={(time: string) => {
                    handleValidation({ currentValue: time, prevValue: value });
                    setValue(time);
                    const answerOption = [
                        {
                            optionId: options?.[0].optionId,
                            optionLabel: options?.[0].optionLabel,
                            answerText: time,
                        },
                    ];
                    handleAnswer?.(formElementId, answerOption);
                }}
                iconDisable={readOnly || !preview}
            />
        </>
    );
};
const WorksheetDateTime: WorksheetFormElementComponent = ({
    item: { options, formElementFile, formElementId, selectedOptions, required },
    readOnly,
    preview,
    onAnswerChange,
    handleIsValid,
}) => {
    const { handleValidation, errorMessage } = useValidation({ handleIsValid, required });

    const [value, setValue] = useState<string>('');

    useEffect(() => {
        const selectedValue = selectedOptions?.[0]?.answerText;
        setValue(selectedValue || '');
    }, [selectedOptions]);
    return (
        <>
            <WorksheetFormFileField formElementFile={formElementFile} editable={false} />
            <InputPickerHelper
                type={'dateTime'}
                iconName={'calendar'}
                data={value}
                errorMessage={errorMessage}
                onChange={(dateTime: string) => {
                    handleValidation({ currentValue: dateTime, prevValue: value });
                    setValue(dateTime);
                    onAnswerChange?.({
                        elementId: formElementId,
                        selectedOptions: [
                            {
                                optionId: options?.[0].optionId,
                                optionLabel: options?.[0].optionLabel,
                                answerText: dateTime,
                            },
                        ],
                    });
                }}
                iconDisable={readOnly || !preview}
            />
        </>
    );
};

const WorksheetFormDate: WorksheetFormElementComponent = ({
    item: { options, formElementFile, required, formElementId, selectedOptions },
    readOnly,
    preview,
    onAnswerChange,
    handleIsValid,
    handleAnswerChange,
}) => {
    const { handleValidation, errorMessage } = useValidation({ handleIsValid, required });
    const { handleAnswer } = useAnswerHandle({ handleAnswerChange, onAnswerChange });
    const [value, setValue] = useState<string>('');
    useEffect(() => {
        const selectedValue = selectedOptions?.[0]?.answerText;
        setValue(selectedValue || '');
    }, [selectedOptions]);
    return (
        <>
            <WorksheetFormFileField formElementFile={formElementFile} editable={false} />
            <InputPickerHelper
                type={'date'}
                iconName={'calendar'}
                data={value}
                errorMessage={errorMessage}
                onChange={(date: string) => {
                    handleValidation({ currentValue: date, prevValue: value });
                    setValue(date);
                    const answer = [
                        {
                            optionId: options?.[0].optionId,
                            optionLabel: options?.[0].optionLabel,
                            answerText: date,
                        },
                    ];
                    handleAnswer(formElementId, answer);
                }}
                iconDisable={!preview || readOnly}
            />
        </>
    );
};

const WorksheetFormRating: WorksheetFormElementComponent = ({
    item: { formElementId, options, formElementFile, selectedOptions, required },
    onAnswerChange,
    readOnly,
    handleIsValid,
    handleAnswerChange,
}) => {
    const { handleAnswer } = useAnswerHandle({ handleAnswerChange, onAnswerChange });
    const [ratingOptions] = useMemo(() => {
        return [
            options.map((option, index) => ({
                id: option.optionId,
                value: index,
                message: option.optionLabel,
            })),
        ];
    }, [options]);
    const selectedOptionIndex = useMemo(() => {
        return ratingOptions.findIndex((option) => option.id === selectedOptions?.[0]?.optionId);
    }, [ratingOptions, selectedOptions]);
    const { handleValidation, errorMessage } = useValidation({ handleIsValid, required });
    const [value, setValue] = useState(selectedOptionIndex !== -1 ? selectedOptionIndex + 1 : undefined);

    return (
        <>
            <WorksheetFormFileField formElementFile={formElementFile} editable={false} />
            <Range
                disabled={readOnly}
                value={value}
                values={ratingOptions}
                onChange={(newValue) => {
                    setValue(newValue);
                    const option = options.find((opt) => opt.optionId === ratingOptions[newValue - 1].id);
                    if (option) {
                        handleValidation({ currentValue: option?.optionId, prevValue: value?.toString() || '' });
                        const answer = [{ optionId: option.optionId, optionLabel: option.optionLabel, answerText: '' }];
                        handleAnswer(formElementId, answer);
                    }
                }}
            />
            <Typography color="redOne" style={{ textAlign: 'center' }}>
                {errorMessage}
            </Typography>
        </>
    );
};

const WorksheetFormRatingWithText: WorksheetFormElementComponent = ({
    item: { formElementId, selectedOptions = [], options, formElementFile, required },
    readOnly,
    onAnswerChange,
    handleIsValid,
    handleAnswerChange,
}) => {
    const { handleAnswer } = useAnswerHandle({ handleAnswerChange, onAnswerChange });
    const [ratingOptions, textareaOption] = useMemo(() => {
        const clone = cloneDeep(options);
        const lastOption = clone.pop();

        return [
            clone.map((option, index) => ({
                id: option.optionId,
                value: index,
                message: option.optionLabel,
            })),
            lastOption,
        ];
    }, [options]);

    const selectedOptionIndex = useMemo(() => {
        const selectedOption = selectedOptions.find((opt) => opt.optionId !== textareaOption?.optionId);
        return ratingOptions.findIndex((option) => option.id === selectedOption?.optionId);
    }, [ratingOptions, selectedOptions, textareaOption?.optionId]);

    const commentText = useMemo(() => {
        const option = selectedOptions.find((opt) => opt.optionId === textareaOption?.optionId);
        if (option) {
            return option.answerText;
        }
        return '';
    }, [selectedOptions, textareaOption?.optionId]);

    const { handleValidation, errorMessage } = useValidation({ handleIsValid, required });

    const [value, setValue] = useState(selectedOptionIndex !== -1 ? selectedOptionIndex + 1 : undefined);
    const [textValue, setTextValue] = useState<string>(commentText || '');
    const [isExpanded, setIsExpanded] = useState(commentText?.trim() === '' ? false : true);
    const [answerOptions, setAnswerOptions] = useState(selectedOptions);
    if (!ratingOptions || !textareaOption) {
        return null;
    }

    const handleRatingAnswer = (option: FormElementOption, text: string) => {
        let answerAnswerClone = cloneDeep(answerOptions);
        const textareaIndex = answerAnswerClone.findIndex((opt) => opt.optionId === textareaOption.optionId);
        const textarea = answerAnswerClone[textareaIndex];
        const answerObj = {
            optionId: option.optionId,
            optionLabel: option.optionLabel,
            answerText: text,
        };
        // If text area option then it available so replace other wise push.
        // Else rating option replace with old option then text area option available push.
        if (textareaOption.optionId !== option.optionId) {
            answerAnswerClone = [answerObj];
            if (textareaIndex > -1) {
                answerAnswerClone.push(textarea);
            }
            handleValidation({ currentValue: option?.optionId, prevValue: value?.toString() || '' });
        } else if (textareaIndex > -1) {
            answerAnswerClone[textareaIndex] = answerObj;
        } else {
            answerAnswerClone.push(answerObj);
        }
        handleAnswer(formElementId, answerAnswerClone as SelectedOption[]);
        setAnswerOptions(answerAnswerClone);
        onAnswerChange?.({
            elementId: formElementId,
            selectedOptions: answerAnswerClone as SelectedOption[],
        });
    };
    return (
        <>
            <WorksheetFormFileField formElementFile={formElementFile} editable={false} />

            <Range
                disabled={readOnly}
                value={value}
                values={ratingOptions}
                onChange={(newValue) => {
                    setValue(newValue);

                    const option = options.find((o) => o.optionId === ratingOptions[newValue - 1].id);
                    if (option) {
                        handleRatingAnswer(option, '');
                    }
                }}
            />
            <Typography color="redOne" style={{ textAlign: 'center' }}>
                {errorMessage}
            </Typography>
            <>
                <TouchableOpacity style={styles.optionalSectionToggle} onPress={() => setIsExpanded(!isExpanded)}>
                    <WorksheetLabel label={textareaOption ? textareaOption.optionLabel : ''} />

                    <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} color="blueOne" />
                </TouchableOpacity>

                <Collapse open={isExpanded}>
                    <TextareaHelper
                        value={textValue}
                        onChangeText={(newValue) => {
                            if (newValue.trim().length > WorksheetCharacterLengthValidation.AnswerLength) {
                                return;
                            }
                            setTextValue(newValue);

                            if (textareaOption) {
                                handleRatingAnswer(textareaOption, newValue);
                            }
                        }}
                        placeholder={textareaOption ? textareaOption.optionPlaceholder : ''}
                    />
                </Collapse>
            </>
        </>
    );
};

const WorksheetFormCheckbox: WorksheetFormElementComponent = ({
    item,
    readOnly,
    preview,
    onAnswerChange,
    handleDragEnd,
    onEditClick,
    onChildDrag,
    onCopyClick,
    onDeleteClick,
    handleIsValid,
    handleAnswerChange,
    handleGridCellAnswerChange,
    getCurrentAnswerByElementId,
}) => {
    const { options, selectedOptions, formElementFile } = item;
    const selectedOption = useMemo(() => {
        return options?.filter((option) => {
            const selectedOpt = selectedOptions?.find((opt) => opt.optionId === option.optionId);
            if (selectedOpt) {
                if (option.isOther) {
                    option.answerText = selectedOpt.answerText;
                }
                return option;
            }
        });
    }, [options, selectedOptions]);
    const [selectedValues, setSelectedValues] = useState<FormElementOption[]>(selectedOption ? selectedOption : []);

    return (
        <>
            <WorksheetFormFileField formElementFile={formElementFile} editable={false} />

            <Options
                item={item}
                readOnly={readOnly}
                preview={preview}
                setSelectedValues={setSelectedValues}
                selectedValues={selectedValues}
                onAnswerChange={onAnswerChange}
                onChildDrag={onChildDrag}
                onCopyClick={onCopyClick}
                onDeleteClick={onDeleteClick}
                onEditClick={onEditClick}
                handleDragEnd={handleDragEnd}
                handleIsValid={handleIsValid}
                handleAnswerChange={handleAnswerChange}
                handleGridCellAnswerChange={handleGridCellAnswerChange}
                getCurrentAnswerByElementId={getCurrentAnswerByElementId}
            />
        </>
    );
};

const WorksheetFormRadio: WorksheetFormElementComponent = ({
    item,
    readOnly,
    onAnswerChange,
    handleDragEnd,
    onEditClick,
    onChildDrag,
    onCopyClick,
    onDeleteClick,
    preview,
    handleIsValid,
    handleAnswerChange,
    getCurrentAnswerByElementId,
}) => {
    const { options, formElementFile, selectedOptions } = item;
    const selectedOption = useMemo(() => {
        return options.filter((option) => {
            if (option.optionId === selectedOptions?.[0]?.optionId) {
                if (option.isOther) {
                    option.answerText = selectedOptions?.[0]?.answerText;
                }
                return option;
            }
        });
    }, [options, selectedOptions]);
    const [selectedValues, setSelectedValues] = useState<FormElementOption[]>(selectedOption ? selectedOption : []);
    return (
        <>
            <WorksheetFormFileField formElementFile={formElementFile} editable={false} />
            <Options
                item={item}
                readOnly={readOnly}
                preview={preview}
                setSelectedValues={setSelectedValues}
                selectedValues={selectedValues}
                onAnswerChange={onAnswerChange}
                onChildDrag={onChildDrag}
                onCopyClick={onCopyClick}
                onDeleteClick={onDeleteClick}
                onEditClick={onEditClick}
                handleDragEnd={handleDragEnd}
                handleIsValid={handleIsValid}
                handleAnswerChange={handleAnswerChange}
                getCurrentAnswerByElementId={getCurrentAnswerByElementId}
            />
        </>
    );
};
interface OptionsProps {
    item: DraggableWorksheetFormComponent;
    readOnly: boolean | undefined;
    preview: boolean | undefined;
    setSelectedValues: React.Dispatch<React.SetStateAction<FormElementOption[]>>;
    selectedValues: FormElementOption[];
    onAnswerChange?: (answer: WorksheetAnswerElement) => void;
    handleDragEnd: (result: DropResult) => void;
    onEditClick?(item: DraggableWorksheetFormComponent): void;
    onDeleteClick?(item: DraggableWorksheetFormComponent): void;
    onCopyClick?(item: DraggableWorksheetFormComponent): void;
    onChildDrag?(item: DraggableWorksheetFormComponent): void;
    handleIsValid?: (valid: boolean) => void;
    handleAnswerChange?: (formelementId: string, selectedAnswer: FormAnswerSelectedOption[]) => void;
    handleGridCellAnswerChange?: (formElementId: string, gridCellAnswer: FormElementGridCellAnswer[]) => void;
    getCurrentAnswerByElementId?: (id: string) => WorksheetElementAnswers;
}
const Options: FC<OptionsProps> = ({
    item,
    readOnly,
    preview,
    setSelectedValues,
    onAnswerChange,
    selectedValues,
    onChildDrag,
    onCopyClick,
    onDeleteClick,
    onEditClick,
    handleDragEnd,
    handleIsValid,
    handleAnswerChange: handleConditionalElementAnswer,
    handleGridCellAnswerChange: handleConditionalElementGridCellAnswer,
    getCurrentAnswerByElementId,
}) => {
    const { options, optionConditionsElements, required, formElementId, formElementTypeId } = item;
    const { handleValidation, errorMessage } = useValidation({ handleIsValid, required });
    const iconName = formElementTypeId === FormElementTypeId.Radio ? 'radio' : 'checkbox';
    const [invalidConditionalElements, setInvalidConditionElements] = useState(0);
    const [optionConditionsElementsClone, setOptionConditionsElementsClone] = useState(optionConditionsElements);
    const { handleAnswer } = useAnswerHandle({
        handleAnswerChange: handleConditionalElementAnswer,
        handleGridCellAnswerChange: handleConditionalElementGridCellAnswer,
        onAnswerChange,
    });

    const otherOptionTextAnswer = useMemo(() => {
        const otherOption = options.find((opt) => opt.isOther);
        if (otherOption) {
            const selectedOtherOption = selectedValues.find((opt) => opt.optionId === otherOption.optionId);
            return selectedOtherOption?.answerText || '';
        } else {
            return '';
        }
    }, [options, selectedValues]);
    const [otherOptionText, setOtherOptionText] = useState(otherOptionTextAnswer || '');

    useEffect(() => {
        setOptionConditionsElementsClone(optionConditionsElements);
    }, [optionConditionsElements]);

    useEffect(() => {
        validateConditionalElements(selectedValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionConditionsElements]);

    const isReadOnlyElement = (element: ConditionalElement, selectedOptionIds: string[]): boolean => {
        const readOnlyFieldElementTypes = [FormElementTypeId.TextHeader, FormElementTypeId.TextParagraph];
        return (
            !selectedOptionIds.includes(element.targetOptionId) ||
            readOnlyFieldElementTypes.includes(element.formElementTypeId)
        );
    };

    const isInputElementType = (elementTypeId: FormElementTypeId): boolean => {
        return [
            FormElementTypeId.InputText,
            FormElementTypeId.InputTextarea,
            FormElementTypeId.Date,
            FormElementTypeId.Time,
            FormElementTypeId.DateTime,
        ].includes(elementTypeId);
    };

    const isGridElementType = (elementTypeId: FormElementTypeId): boolean => {
        return [
            FormElementTypeId.DynamicGrid,
            FormElementTypeId.PressureTestGrid,
            FormElementTypeId.ValveClearanceGrid,
            FormElementTypeId.VehicleNoiseGrid,
        ].includes(elementTypeId);
    };

    const isFileElementUnanswered = (element: DraggableWorksheetFormComponent): boolean => {
        return !element.answerAssets?.length;
    };

    const isInputElementUnanswered = (element: DraggableWorksheetFormComponent): boolean => {
        if (!element.selectedOptions?.length) {
            return true;
        }

        const answerText = element.selectedOptions?.[0]?.answerText;

        if (answerText === undefined) {
            return true;
        }

        return answerText === '';
    };

    const isRatingWithTextUnanswered = (element: DraggableWorksheetFormComponent): boolean => {
        if (!element.selectedOptions?.length) {
            return true;
        }

        const textAreaOption = element.options[element.options.length - 1];
        const textAreaOptionId = textAreaOption?.optionId;

        if (element.selectedOptions.length === 1) {
            // Only one selection — unanswered if it's just the textarea (no rating picked)
            return element.selectedOptions[0].optionId === textAreaOptionId;
        }

        // Multiple selections — find the rating option and check it has a label
        const ratingOption = element.selectedOptions.find((opt) => opt.optionId !== textAreaOptionId);
        return ratingOption?.optionLabel === '';
    };

    const isElementUnanswered = (element: DraggableWorksheetFormComponent): boolean => {
        const { formElementTypeId } = element;

        if (formElementTypeId === FormElementTypeId.File) {
            return isFileElementUnanswered(element);
        }

        if (isInputElementType(formElementTypeId)) {
            return isInputElementUnanswered(element);
        }

        if (formElementTypeId === FormElementTypeId.RatingWithText) {
            return isRatingWithTextUnanswered(element);
        }

        if (isGridElementType(formElementTypeId)) {
            return hasUnansweredRequiredCells(
                element.formElementTypeId,
                element.grid,
                element.worksheetGridCellAnswers,
                element.required
            );
        }

        if (element.selectedOptions?.length) {
            return element.selectedOptions[0].optionLabel === '';
        }

        return true;
    };

    const validateConditionalElements = (selectedOption: FormElementOption[]) => {
        if (!selectedOption.length || !optionConditionsElementsClone?.length) {
            return;
        }

        const selectedOptionIds = selectedOption.map((selected) => selected.optionId);

        const unansweredRequiredCount = optionConditionsElementsClone.reduce((count, element) => {
            if (isReadOnlyElement(element, selectedOptionIds)) {
                return count;
            }
            if (!element.required) {
                return count;
            }
            return isElementUnanswered(element) ? count + 1 : count;
        }, 0);

        if (unansweredRequiredCount) {
            handleIsValid?.(false);
        }
        setInvalidConditionElements(unansweredRequiredCount);
    };

    const handleInvalidCount = (valid: boolean) => {
        let isRadioValid = false;
        let isChange = false;
        // When field is valid decrease one count in invalidCount or not a valid than increase one in inValidFiledCount
        let invalidCountClone = invalidConditionalElements;
        if (valid) {
            invalidCountClone--;
        } else {
            invalidCountClone++;
        }
        if (!(invalidConditionalElements && invalidCountClone) && handleIsValid) {
            if (invalidConditionalElements && !invalidCountClone) {
                isRadioValid = true;
            }

            isChange = true;
        }

        setInvalidConditionElements(invalidCountClone);

        if (isChange && handleIsValid) {
            handleIsValid(isRadioValid);
        }
    };

    // Handle when change selected option in optionConditional element.
    const handleAnswerChange = (id: string, selectedAnswer: FormAnswerSelectedOption[]) => {
        const clone = cloneDeep(optionConditionsElementsClone);
        clone?.forEach((ele) => {
            if (ele.formElementId === id) {
                ele.selectedOptions = selectedAnswer;
            }
        });

        setOptionConditionsElementsClone(clone);
    };

    const handleConditionalChildGridCellAnswerChange = (id: string, gridCellAnswers: FormElementGridCellAnswer[]) => {
        const clone = cloneDeep(optionConditionsElementsClone);
        clone?.forEach((element) => {
            if (element.formElementId === id) {
                element.worksheetGridCellAnswers = gridCellAnswers;
            }
        });
        setOptionConditionsElementsClone(clone);
    };

    // Handle parent onAnswerChange or handleAnswer
    const handleOnAnswerChange = (selectedOptions: FormElementOption[]) => {
        const answer = selectedOptions.map((opt) => ({
            optionId: opt.optionId,
            optionLabel: opt.optionLabel,
            answerText: opt.isOther ? opt.answerText || '' : '',
        }));
        handleAnswer(formElementId, answer);
        setSelectedValues(selectedOptions);
    };
    return (
        <>
            {options.map((option: FormElementOption) => {
                const isOptionSelected = !!selectedValues?.find(
                    (selectedOption) => selectedOption?.optionId === option.optionId
                );
                const optionConditions =
                    optionConditionsElementsClone?.filter((element) => element.targetOptionId === option.optionId) ||
                    [];
                const conditions: string = optionConditions?.map((element) => element.formElementOrder).join('-');
                return (
                    <Fragment key={option.optionId}>
                        <View
                            style={[preview ? { marginBottom: 8 } : styles.inputContainer, { flexDirection: 'row' }]}
                            key={option.optionId}
                        >
                            <TouchableOpacity
                                disabled={readOnly}
                                style={[
                                    styles.selectOptionWrapper,
                                    {
                                        justifyContent: option.isOther ? 'flex-start' : 'space-between',
                                        flexWrap: option.isOther ? 'wrap' : 'nowrap',
                                    },
                                ]}
                            >
                                <div
                                    style={{ display: 'flex', alignItems: 'center' }}
                                    onClick={() => {
                                        let selectedValuesClone = cloneDeep(selectedValues);
                                        // store selected option length for validation, it is length before any changes in selectedValues
                                        const selectedOptionLength = selectedValues.length;
                                        if (item.formElementTypeId === FormElementTypeId.Checkbox) {
                                            // If already selected checkbox
                                            if (isOptionSelected) {
                                                selectedValuesClone = selectedValuesClone.filter(
                                                    (selectedOption) => selectedOption?.optionId !== option.optionId
                                                );
                                            } else {
                                                selectedValuesClone.push(option);
                                            }
                                        } else {
                                            selectedValuesClone = [option];
                                        }

                                        handleValidation({
                                            currentValue: selectedValuesClone.length
                                                ? selectedValuesClone.length.toString()
                                                : '',
                                            prevValue: selectedOptionLength ? selectedOptionLength.toString() : '',
                                        });
                                        if (invalidConditionalElements) {
                                            handleIsValid?.(true);
                                        }
                                        validateConditionalElements(selectedValuesClone);
                                        handleOnAnswerChange(selectedValuesClone);
                                    }}
                                >
                                    <Icon
                                        size={28}
                                        name={
                                            (isOptionSelected
                                                ? `${iconName}-marked`
                                                : `${iconName}-unmarked`) as StsIconName
                                        }
                                        color={selectedValues ? 'blueOne' : 'grayFive'}
                                    />
                                    <WorksheetLabel
                                        label={`${option.optionLabel} ${option.isOther && isOptionSelected ? ':' : ''}`}
                                        style={{ marginLeft: 8, marginBottom: 0 }}
                                    />
                                </div>
                                {option.isOther && isOptionSelected ? (
                                    <textarea
                                        rows={1}
                                        ref={(el) => {
                                            if (!el) {
                                                return;
                                            }
                                            el.style.height = 'auto';
                                            el.style.height = `${el.scrollHeight}px`;
                                        }}
                                        style={{
                                            border: 0,
                                            borderBottom: '1px solid black',
                                            minWidth: '90%',
                                            width: '100%',
                                            resize: 'none',
                                            overflow: 'hidden',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'keep-all',
                                            overflowWrap: 'normal',
                                        }}
                                        value={otherOptionText}
                                        maxLength={WorksheetCharacterLengthValidation.AnswerLength}
                                        onInput={(e) => {
                                            const target = e.currentTarget;
                                            target.style.height = 'auto';
                                            target.style.height = `${target.scrollHeight}px`;
                                        }}
                                        onChange={(e) => {
                                            const text = e.target.value;
                                            const selectedValueClone = cloneDeep(selectedValues);
                                            selectedValueClone.forEach((opt) => {
                                                if (opt.isOther) {
                                                    opt.answerText = text;
                                                }
                                            });
                                            handleOnAnswerChange(selectedValueClone);
                                            setOtherOptionText(text);
                                        }}
                                        readOnly={readOnly}
                                    />
                                ) : (
                                    <></>
                                )}
                                {!preview && option?.trgtElementIdList && option?.trgtElementIdList?.length > 0 && (
                                    <TextBadge variant="blue" textStyle={{ color: colors.blueOneActive }}>
                                        Conditional {conditions}
                                    </TextBadge>
                                )}
                            </TouchableOpacity>
                            {option.optionFile && (
                                <div style={{ marginLeft: 10 }}>
                                    <WorksheetFormFileField formElementFile={option.optionFile} editable={false} />
                                </div>
                            )}
                        </View>
                        {preview &&
                            isOptionSelected &&
                            optionConditionsElementsClone &&
                            optionConditionsElementsClone.length > 0 && (
                                <>
                                    <div style={{ marginLeft: 36 }}>
                                        {optionConditionsElementsClone?.map((element) => {
                                            if (element.targetOptionId === option.optionId) {
                                                return (
                                                    <BaseWorksheetFormElement
                                                        item={element}
                                                        onAnswerChange={onAnswerChange}
                                                        handleDragEnd={handleDragEnd}
                                                        key={element.formElementId}
                                                        readOnly={readOnly}
                                                        preview={preview}
                                                        handleIsValid={handleInvalidCount}
                                                        handleAnswerChange={handleAnswerChange}
                                                        handleGridCellAnswerChange={
                                                            handleConditionalChildGridCellAnswerChange
                                                        }
                                                        getCurrentAnswerByElementId={getCurrentAnswerByElementId}
                                                    />
                                                );
                                            }
                                        })}
                                    </div>
                                </>
                            )}

                        {!preview && optionConditionsElements && optionConditionsElements.length > 0 && (
                            <ConditionalOptions
                                parentItem={item}
                                handleDragEnd={handleDragEnd}
                                onChildDrag={onChildDrag}
                                onCopyClick={onCopyClick}
                                onDeleteClick={onDeleteClick}
                                onEditClick={onEditClick}
                                optionConditionsElements={optionConditionsElements}
                                option={option}
                                onAnswerChange={onAnswerChange}
                            />
                        )}
                    </Fragment>
                );
            })}
            <Typography color="redOne" style={{ textAlign: 'center' }}>
                {errorMessage}
            </Typography>
        </>
    );
};

const WorksheetDropdown: WorksheetFormElementComponent = ({
    item,
    onAnswerChange,
    readOnly,
    handleIsValid,
    handleAnswerChange,
}) => {
    const { options, selectedOptions, formElementFile, required, formElementId } = item;
    const { handleValidation, errorMessage } = useValidation({ handleIsValid, required });
    const { handleAnswer } = useAnswerHandle({ handleAnswerChange, onAnswerChange });

    const [value, setValue] = useState<string>(selectedOptions?.[0]?.optionId || '');

    return (
        <>
            <WorksheetFormFileField formElementFile={formElementFile} editable={false} />
            {readOnly ? (
                <div className="d-flex mt-2 mb-8">
                    <SuccessFailBadge success>{selectedOptions?.[0]?.optionLabel}</SuccessFailBadge>
                </div>
            ) : (
                <div className="mt-2 mb-8">
                    <Select
                        required
                        value={value}
                        options={
                            options
                                ? [
                                      { title: 'Select...', value: '', disabled: true },
                                      ...options.map((option) => ({
                                          title: option.optionLabel,
                                          value: option.optionId,
                                      })),
                                  ]
                                : []
                        }
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                            const newVal = event.target.value;
                            const option = options.find((opt) => opt.optionId === newVal);
                            if (option) {
                                handleValidation({ currentValue: newVal, prevValue: value });
                                setValue(newVal);
                                const answer = [
                                    {
                                        optionId: option.optionId,
                                        optionLabel: option.optionLabel,
                                        answerText: '',
                                    },
                                ];
                                handleAnswer(formElementId, answer);
                            }
                        }}
                    />
                </div>
            )}
            <Typography color="redOne" style={{ textAlign: 'center' }}>
                {errorMessage}
            </Typography>
        </>
    );
};

const WorksheetFormTireWearRadio: WorksheetFormElementComponent = ({
    item,
    readOnly,
    onAnswerChange,
    handleIsValid,
    handleAnswerChange,
}) => {
    const { options, selectedOptions, formElementId, required } = item;
    const { handleValidation, errorMessage } = useValidation({ handleIsValid, required });
    const { handleAnswer } = useAnswerHandle({ handleAnswerChange, onAnswerChange });
    const [value, setValue] = useState<string>(selectedOptions?.[0]?.optionId || '');

    const onSelect = useCallback(
        (option: FormElementOption) => {
            const nextValue = option.optionId;
            const prevValue = value;

            if (nextValue === prevValue) {
                return;
            }

            handleValidation({ currentValue: nextValue, prevValue: prevValue });

            handleAnswer(formElementId, [
                { optionId: option.optionId, optionLabel: option.optionLabel, answerText: '' },
            ]);

            setValue(nextValue);
        },
        [formElementId, handleAnswer, handleValidation, value]
    );

    return (
        <>
            <TireWearRadio options={options} value={value} onChange={onSelect} disabled={readOnly} />
            <Typography color="redOne" style={{ textAlign: 'center' }}>
                {errorMessage}
            </Typography>
        </>
    );
};

const WorksheetGridImageNoise: WorksheetFormElementComponent = ({
    item,
    readOnly,
    onAnswerChange,
    handleIsValid,
    handleGridCellAnswerChange,
}) => {
    const classes = getGridStyles();
    const { formElementId, formElementTypeId, grid, worksheetGridCellAnswers, required } = item;
    const { gridCellAnswers, handleCellSelect } = useGridElement({
        formElementId,
        formElementTypeId,
        required,
        grid: grid as DynamicGridRowType[],
        worksheetGridCellAnswers,
        onAnswerChange,
        handleIsValid,
        handleGridCellAnswerChange,
    });

    const alphaRange = (start: string, stop: string) => {
        const result: string[] = [];
        for (let i = start.charCodeAt(0); i <= stop.charCodeAt(0); i++) {
            result.push(String.fromCharCode(i));
        }
        return result;
    };

    const xLabels = alphaRange('A', 'D');
    const yLabels = alphaRange('1', '3');

    const handleCellClick = (rowLabel: string, colLabel: string) => {
        if (readOnly) {
            return;
        }
        const row = grid?.find((r) => r.rowLabel === rowLabel);
        if (!row) return;

        let targetSubColumn: any;

        row.columns.forEach((column) => {
            column?.subColumns?.forEach((sub) => {
                if (sub.subColumnLabel === colLabel) {
                    targetSubColumn = sub;
                }
            });
        });

        if (!targetSubColumn) return;

        const worksheetElementGridId = targetSubColumn.worksheetElementGridId;

        const existingAnswer = gridCellAnswers?.find(
            (answer) => answer.worksheetElementGridId === worksheetElementGridId
        );

        const isSelected = existingAnswer?.value === 'selected';

        handleCellSelect(worksheetElementGridId, isSelected ? '' : 'selected');
    };

    return (
        <div className={classes.gridWrapper}>
            <div className={classes.gridLayout}>
                <table id="grid" className={classes.grid}>
                    <tbody>
                        {yLabels.map((rowLabel, rowIndex) => (
                            <tr key={rowIndex}>
                                {xLabels.map((colLabel, colIndex) => {
                                    const row = grid?.find((r) => r.rowLabel === rowLabel);

                                    let subColumn: any;
                                    row?.columns.forEach((column) => {
                                        column.subColumns?.forEach((sub) => {
                                            if (sub.subColumnLabel === colLabel) {
                                                subColumn = sub;
                                            }
                                        });
                                    });

                                    const worksheetElementGridId = subColumn?.worksheetElementGridId;

                                    const isSelected = gridCellAnswers?.some(
                                        (answer) =>
                                            answer.worksheetElementGridId === worksheetElementGridId &&
                                            answer.value === 'selected'
                                    );

                                    return (
                                        <td
                                            key={colIndex}
                                            className={classes.gridCell}
                                            onClick={() => handleCellClick(rowLabel, colLabel)}
                                            style={{
                                                backgroundColor: isSelected
                                                    ? 'rgba(67, 108, 161, 0.50)'
                                                    : 'transparent',
                                                cursor: readOnly ? 'default' : 'pointer',
                                            }}
                                        >
                                            {colIndex === 0 && <span className={classes.yLabels}>{rowLabel}</span>}
                                            {rowIndex === 0 && <span className={classes.xLabels}>{colLabel}</span>}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className={classes.groupLabelsRow}>
                    <div className={classes.groupLabel} style={{ gridColumn: '2 / 3' }}>
                        General
                    </div>
                    <div className={classes.groupLabel} style={{ gridColumn: '5 / 5' }}>
                        Bottom
                    </div>
                </div>
            </div>
        </div>
    );
};

const WorksheetFormDynamicGrid: WorksheetFormElementComponent = ({
    item,
    preview,
    readOnly,
    onAnswerChange,
    handleIsValid,
    handleGridCellAnswerChange,
}) => {
    const {
        formElementId,
        formElementTypeId,
        formElementPlaceholder = '',
        grid,
        worksheetGridCellAnswers,
        required,
    } = item;

    const { gridCellAnswers, isCellInvalid, handleCellChange, handleCellBlur } = useGridElement({
        formElementId,
        formElementTypeId,
        required,
        grid: grid as DynamicGridRowType[],
        worksheetGridCellAnswers,
        onAnswerChange,
        handleIsValid,
        handleGridCellAnswerChange,
    });

    return (
        <div style={!preview ? { maxWidth: 650 } : undefined}>
            <DynamicGrid
                cornerHeader={formElementPlaceholder}
                rows={grid as DynamicGridRowType[]}
                readOnly={readOnly}
                answers={gridCellAnswers}
                onChange={handleCellChange}
                onBlur={handleCellBlur}
                isCellInvalid={isCellInvalid}
            />
        </div>
    );
};

const WorksheetFormPressureTestGrid: WorksheetFormElementComponent = ({
    item,
    readOnly,
    onAnswerChange,
    handleIsValid,
    handleGridCellAnswerChange,
}) => {
    const {
        formElementId,
        formElementTypeId,
        formElementPlaceholder = '',
        grid,
        worksheetGridCellAnswers,
        required,
    } = item;

    const { gridCellAnswers, isCellInvalid, handleCellChange, handleCellBlur } = useGridElement({
        formElementId,
        formElementTypeId,
        grid: grid as SubRowGridType[],
        worksheetGridCellAnswers,
        onAnswerChange,
        handleIsValid,
        handleGridCellAnswerChange,
        required,
    });

    return (
        <div>
            <PressureTestGrid
                cornerHeader={formElementPlaceholder}
                rows={grid as SubRowGridType[]}
                readOnly={readOnly}
                answers={gridCellAnswers}
                onChange={handleCellChange}
                onBlur={handleCellBlur}
                isCellInvalid={isCellInvalid}
            />
        </div>
    );
};

const WorksheetFormValveClearanceGrid: WorksheetFormElementComponent = ({
    item,
    readOnly,
    onAnswerChange,
    handleIsValid,
    handleGridCellAnswerChange,
}) => {
    const {
        formElementId,
        formElementTypeId,
        formElementPlaceholder = '',
        grid,
        worksheetGridCellAnswers,
        required,
    } = item;

    const { gridCellAnswers, isCellInvalid, handleCellChange, handleCellBlur } = useGridElement({
        formElementId,
        formElementTypeId,
        grid: grid as SubColumnGridType[],
        worksheetGridCellAnswers,
        required,
        onAnswerChange,
        handleIsValid,
        handleGridCellAnswerChange,
    });

    return (
        <div>
            <ValveClearanceGrid
                cornerHeader={formElementPlaceholder}
                rows={grid as SubColumnGridType[]}
                readOnly={readOnly}
                answers={gridCellAnswers}
                onChange={handleCellChange}
                onBlur={handleCellBlur}
                isCellInvalid={isCellInvalid}
            />
        </div>
    );
};

export const WorksheetFormElements = {
    [FormElementTypeId.TextHeader]: WorksheetFormTextHeader,
    [FormElementTypeId.TextParagraph]: WorksheetFormTextParagraph,
    [FormElementTypeId.InputText]: WorksheetFormInputText,
    [FormElementTypeId.InputTextarea]: WorksheetFormInputTextarea,
    [FormElementTypeId.Rating]: WorksheetFormRating,
    [FormElementTypeId.RatingWithText]: WorksheetFormRatingWithText,
    [FormElementTypeId.Checkbox]: WorksheetFormCheckbox,
    [FormElementTypeId.Radio]: WorksheetFormRadio,
    [FormElementTypeId.File]: WorksheetFormFile,
    [FormElementTypeId.Date]: WorksheetFormDate,
    [FormElementTypeId.Time]: WorksheetTime,
    [FormElementTypeId.DateTime]: WorksheetDateTime,
    [FormElementTypeId.Dropdown]: WorksheetDropdown,
    [FormElementTypeId.TireWear]: WorksheetFormTireWearRadio,
    [FormElementTypeId.VehicleNoiseGrid]: WorksheetGridImageNoise,
    [FormElementTypeId.DynamicGrid]: WorksheetFormDynamicGrid,
    [FormElementTypeId.PressureTestGrid]: WorksheetFormPressureTestGrid,
    [FormElementTypeId.ValveClearanceGrid]: WorksheetFormValveClearanceGrid,
};

const styles = StyleSheet.create({
    imageContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '10px',
        alignItems: 'center',
        flexDirection: 'row',
        marginTop: 8,
        position: 'relative',
    },
    labelText: {
        marginBottom: 8,
        //@ts-ignore
        wordBreak: 'break-word',
    },
    optionalSectionToggle: {
        marginTop: 8,
        minHeight: 48,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    selectOptionWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        wordBreak: 'break-all',
        alignContent: 'center',
    },
    inputContainer: {
        minHeight: Platform.select({ web: 40, default: 48 }),
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderRadius: 5,
        borderWidth: 1,
        borderColor: colors.grayThree,
        marginVertical: 5,
        border: `1px solid ${colors.grayFour}`,
    },
});

const getGridStyles = createUseStyles({
    grid: {
        backgroundSize: '100% 100%',
        width: 'calc(32vw)',
        height: 'calc(20vw)',
        backgroundImage: `url(${gridImage})`,
        borderCollapse: 'collapse',
        marginLeft: '56px',
        gridColumn: '1 / -1',
        tableLayout: 'fixed',
    },
    gridRow: {
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
    },
    gridCell: {
        border: '1px solid #000',
        position: 'relative',
    },
    xLabels: {
        position: 'absolute',
        left: 'calc(50% - 6px)',
        pointerEvents: 'none',
        top: '-28px',
        fontWeight: 500,
    },
    yLabels: {
        position: 'absolute',
        left: '-20px',
        pointerEvents: 'none',
        top: '15px',
        fontWeight: 500,
    },
    vLabel: {
        position: 'absolute',
        top: '45px',
        left: '39px',
        fontWeight: 'bold',
        fontSize: '20px',
    },
    secondCodeLabel: {
        position: 'absolute',
        top: '0',
        right: '29%',
        fontWeight: 'bold',
        fontSize: '16px',
    },
    thirdCodeLabel: {
        position: 'absolute',
        transform: 'rotate(90deg)',
        top: '50%',
        left: '-78px',
        fontWeight: 'bold',
        fontSize: '16px',
    },
    gridWrapper: {
        marginTop: '20px',
        maxHeight: 'calc(600px + 22px)',
        minHeight: '622px',
        overflow: 'auto',
        paddingTop: '70px',
        position: 'relative',
    },
    groupLabelsRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        width: 'calc(32vw)',
        marginTop: 8,
        marginLeft: '56px',
    },
    groupLabel: {
        textAlign: 'center',
        fontWeight: 500,
    },
    gridLayout: {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        width: 'calc(32vw)',
    },
});
